import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card } from "../common/Card";
import { ErrorRetry } from "../../common/ErrorRetry";
import { DailyRevenueShimmer } from "../../common/reportDashBShimmer/DailyRevenueShimmer";
import { PieChartShimmer } from "../../common/reportDashBShimmer/PieChartShimmer";
import {
  PACKAGE_CHART_COLORS,
  formatPackageMoney,
  formatPackagePercentage,
} from "./packageReportUtils";

export function PackageRevenueOverviewSection({
  trendsState,
  breakdownsState,
  monthlyTrend,
  categoryBreakdown,
}) {
  const renderCategoryTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const category = payload[0]?.payload;

    if (!category) {
      return null;
    }

    return (
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 shadow-sm">
        <div className="text-sm font-medium text-[#1A1A1A]">{category.name}</div>
        <div className="mt-1 text-sm text-[#1A1A1A]">
          {formatPackageMoney(category.value)}
        </div>
        <div className="text-xs text-[#6B7280]">
          {category.transactions || 0} transactions
        </div>
        {category.description ? (
          <div className="mt-2 max-w-[240px] text-xs text-[#6B7280]">
            {category.description}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      <span className="mb-4 text-2xl text-[#1A1A1A]">Revenue Overview</span>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-xl text-[#1A1A1A]">
            Monthly Revenue & Bookings Trend
          </span>

          {trendsState.loading ? (
            <DailyRevenueShimmer />
          ) : trendsState.error ? (
            <div className="h-[350px] flex items-center justify-center">
              <ErrorRetry
                message={trendsState.message}
                onRetry={trendsState.onRetry}
              />
            </div>
          ) : monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis yAxisId="left" stroke="#6B7280" />
                <YAxis yAxisId="right" orientation="right" stroke="#6B7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                  formatter={(value, name) =>
                    name === "Revenue (NGN)"
                      ? formatPackageMoney(value)
                      : Number(value).toLocaleString()
                  }
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#1E61DC"
                  strokeWidth={3}
                  name="Revenue (NGN)"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="bookings"
                  stroke="#10B981"
                  strokeWidth={3}
                  name="Bookings"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No revenue trend data available
            </div>
          )}
        </Card>

        {breakdownsState.loading ? (
          <PieChartShimmer />
        ) : categoryBreakdown.length > 0 ? (
          <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <span className="mb-6 text-xl text-[#1A1A1A]">
              Category Breakdown
            </span>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={categoryBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, sharePercentage }) =>
                    `${name} ${formatPackagePercentage(sharePercentage)}`
                  }
                >
                  {categoryBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        PACKAGE_CHART_COLORS[
                          index % PACKAGE_CHART_COLORS.length
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={renderCategoryTooltip}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
            <div
              className={`h-[350px] flex items-center justify-center ${
                breakdownsState.error ? "text-red-500" : "text-gray-500"
              }`}
            >
              {breakdownsState.error ? (
                <ErrorRetry
                  message={breakdownsState.message}
                  onRetry={breakdownsState.onRetry}
                />
              ) : (
                "No category breakdown available"
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
